import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChildren} from '@angular/core';
import {DeliveryProduct} from '../../shared/model/delivery-product';
import {MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow,
  MatRowDef, MatRow} from '@angular/material/table';
import {MatIcon} from '@angular/material/icon';
import {RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';
import {Platform} from '@angular/cdk/platform';
import {ChangeCategoriesComponent} from '../change-categories/change-categories.component';
import {DeliveryBasesComponent} from '../delivery-bases/delivery-bases.component';
import {YesNoPipe} from '../../shared/pipes/yes-no.pipe';
import {TranslatePipe} from '@ngx-translate/core';
import {FocusKeyManager} from '@angular/cdk/a11y';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {FocusableTableItemDirective} from '../../shared/directive/focusable-table-item.directive';

@Component({
  selector: 'myupdates-delivery-products-table-view',
  templateUrl: './delivery-products-table-view.component.html',
  styleUrls: ['./delivery-products-table-view.component.scss'],
  imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, ChangeCategoriesComponent,
    DeliveryBasesComponent, RouterLink, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, NgClass, MatIcon, TranslatePipe,
    YesNoPipe, FocusableTableItemDirective]
})
export class DeliveryProductsTableViewComponent implements AfterViewInit, OnDestroy {
  @Input() products: DeliveryProduct[] = [];
  @Input() displayDeliveryBasesAndDataTransformation = true;
  @Input() changeCategoriesVerticalOrientation = false;
  @Input() selectedProduct: DeliveryProduct | null = null;
  @Input() enableInlineSelection = false;

  @Output() productSelected = new EventEmitter<DeliveryProduct>();

  @ViewChildren(FocusableTableItemDirective) focusableLinks!: QueryList<FocusableTableItemDirective>;

  displayedColumns: string[] = ['productTitle', 'productChangeCategories', 'productDeliveryBases', 'productDataTransformation', 'productLink'];
  reducedColumns: string[] = ['productTitle', 'productChangeCategories', 'productLink'];

  private keyManager!: FocusKeyManager<FocusableTableItemDirective>;
  private destroy$ = new Subject<void>();

  constructor(public platform: Platform, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.initKeyManager();

    this.focusableLinks.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initKeyManager());
  }

  ngOnDestroy(): void {
    this.keyManager?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initKeyManager(): void {
    this.keyManager?.destroy();

    if (!this.focusableLinks.length) return;

    const items = this.focusableLinks.toArray();

    this.keyManager = new FocusKeyManager(items)
      .withVerticalOrientation()
      .withHomeAndEnd()
      .withWrap();

    this.keyManager.change
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeIndex: number) => {
        items.forEach((item, idx) => item.setActive(idx === activeIndex));
        this.cdr.detectChanges();
      });

    items.forEach((link, index) => {
      link.linkFocused
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.keyManager.setActiveItem(index);
        });
    });

    // Setze das erste Item als aktiv, aber erst nach einem Mikrotask
    // So wird verhindert, dass während der Initialisierung fokussiert wird und ein NG0100 kommt
    Promise.resolve().then(() => {
      if (this.keyManager && items.length > 0) {
        this.keyManager.updateActiveItem(0);
        items[0].setActive(true);
      }
    });
  }

  selectProduct(element: DeliveryProduct): void {
    if (element.documentId || element.documentUrl) {
      this.productSelected.emit(element);
    }
  }

  onLinkClick(event: Event, element: DeliveryProduct): void {
    if (this.enableInlineSelection) {
      event.preventDefault();
      this.selectProduct(element);
    }
  }

  onLinkKeyDown(event: KeyboardEvent, element: DeliveryProduct): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.enableInlineSelection) {
        this.selectProduct(element);
      } else {
        if (event.currentTarget instanceof HTMLElement) {
          event.currentTarget.click();
        }
      }
    }
  }

  onTableKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') return;

    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      this.keyManager?.onKeydown(event);
      this.keyManager?.activeItem?.focus();
    }
  }

  focusProductLink(productId: string): void {
    const links = this.focusableLinks.toArray();
    const index = links.findIndex(link =>
      link.element.id === `a_${productId}`
    );

    if (index >= 0) {
      this.keyManager.setActiveItem(index);
      links[index].focus();
    }
  }

}

